import Sidebar from './sidebar';
import GaiaChatWidget from './GaiaChatWidget';

import bmwLogoImageSrc from '../images/bmw-logo-new-white.svg';
import {
  getLoginViaWebEAMUrlAlpha,
  getCurrentUserName,
  initJwt,
  logoutFromIdp,
  setLoginProvider,
} from './js/portal/auth';
import { linkWithoutSearch, showTempPrivilegeEscalationRequestModal } from './js/portal/main';

export default function App() {
  // ---------- Auth migration guard ----------
  // When rolling out the Alpha OIDC migration, existing users may still have
  // old Cognito tokens / session data in their browser.  If the auth version
  // marker is missing (or belongs to an older version) we wipe ALL storage so
  // the user starts fresh with the new Alpha OIDC flow.
  const AUTH_VERSION = 'alpha-oidc-v1'; // bump this to force re-auth on future auth changes
  if (localStorage.getItem('auth_version') !== AUTH_VERSION) {
    localStorage.clear();
    sessionStorage.clear();

    // Stamp the new version so this block won't run again
    localStorage.setItem('auth_version', AUTH_VERSION);

    // Redirect to Alpha OIDC login
    getLoginViaWebEAMUrlAlpha(window.location.hash, window.location.search, window.location.pathname).then(loginUrl => {
      window.location.href = loginUrl;
    });
    return;
  }
  // ---------- End migration guard ----------

  const urlParams = new URLSearchParams(window.location.search);

  // Search functionality
  const handleSearch = () => {
    const searchInput = document.getElementById('general-search-input');
    const query = searchInput?.value?.trim();

    if (query) {
      const encodedQuery = encodeURIComponent(query);

      // If already on the search page, dispatch event to update without full page reload
      if (window.location.pathname.includes('/search/general-search')) {
        // Update URL without reload using history API
        const newUrl = `/search/general-search?query=${encodedQuery}`;
        window.history.pushState({ query }, '', newUrl);

        // Dispatch event for the search service micro-frontend to pick up
        window.dispatchEvent(
          new CustomEvent('portal-search-query', {
            detail: { query },
          }),
        );
      } else {
        // Navigate to search page
        window.location.href = `/search/general-search?query=${encodedQuery}`;
      }
    }
  };

  const handleKeyPress = event => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  // Listen for search service events to sync the navbar search input
  if (typeof window !== 'undefined') {
    window.addEventListener('search-service-query', event => {
      const searchInput = document.getElementById('general-search-input');
      if (searchInput && event.detail?.query !== undefined) {
        searchInput.value = event.detail.query;
      }
    });
  }

  const hasAuthToken = !!sessionStorage.getItem('user_token');

  // If we landed on /logout (from OIDC post_logout_redirect_uri), redirect
  // to the root so the login flow doesn't encode "/logout" into state.
  if (window.location.pathname === '/logout') {
    window.location.href = '/';
    return;
  }

  // If the user has /#login or #login bookmarked, rewrite the hash to
  // #dashboard and let the normal auth flow continue (which will either
  // redirect to login if needed, or navigate to the dashboard).
  if (window.location.hash === '#login') {
    window.location.hash = '#dashboard';
  }

  if (!hasAuthToken && !urlParams.has('code')) {
    // If there's no valid user login yet
    console.debug('Login required');

    // Remove cached feature flags from session storage
    Object.keys(sessionStorage).forEach(function (key) {
      if (key.startsWith('FeatureFlag.')) sessionStorage.removeItem(key);
    });

    // Forward the user to the login page
    getLoginViaWebEAMUrlAlpha(window.location.hash, window.location.search, window.location.pathname).then(loginUrl => {
      window.location.href = loginUrl;
    });
    return;
  } else if (urlParams.has('code')) {
    // After the initial login, the user gets a code from the IdP
    setLoginProvider('idp');

    let targetHash = sessionStorage.lastHash || '#dashboard';
    let targetSearch = '';
    let targetPath = '/';
    const encodedState = urlParams.get('state');

    if (encodedState) {
      try {
        const state = JSON.parse(atob(encodedState));

        if (state.target) {
          const { target } = JSON.parse(atob(state.target));
          if (target && target.startsWith('#')) {
            targetHash = target;
          } else {
            console.warn(`Invalid target: "${target}"! Target must start with a hash`);
          }
        }

        if (state.search) {
          const { search } = JSON.parse(atob(state.search));
          if (search && search.startsWith('?')) {
            targetSearch = search;
          } else {
            console.warn(`Invalid search: "${search}"! Search must start with a question mark`);
          }
        }

        if (state.path) {
          const { path } = JSON.parse(atob(state.path));
          if (path) {
            targetPath = path;
          }
        }
      } catch (err) {
        console.warn('Invalid state returned from identity provider: ', encodedState);
      }
    }

    initJwt(urlParams.get('code')).then(async () => {
      // For micro-frontend paths (e.g. /idc/list-roles), don't append a hash
      // — only hash-based routes (served from /) use the targetHash.
      const isMicroFrontend = targetPath !== '/' && targetPath !== '/aws/index.html';
      let targetUrl = isMicroFrontend ? targetPath + targetSearch : targetPath + targetSearch + targetHash;
      window.open(targetUrl, '_self');
    });
    return;
  } else {
    // And finally, after login was successful, the user can start the navigation, by default to the dashboard
    if (window.location.hash) sessionStorage.lastHash = window.location.hash;
    if (!sessionStorage.lastHash && window.location.pathname === '/') window.location.href = '/#dashboard';
  }

  return (
    <>
      {/* Navigation */}
      <nav class="navbar navbar-dark navbar-inverse fixed-top" role="navigation">
        {/* Top Navigation: Text and Icon */}
        <a class="navbar-brand" href="#dashboard">
          <img src={bmwLogoImageSrc} width="25" class="d-inline-block" />
          <span class="d-none d-md-inline-block display-brand-name">{process.env.PORTAL_CONFIG.headerText}</span>
          <span class="d-md-none display-brand-name-short">BMW | AWS</span>
        </a>
        {/* Top Navigation: Right Menu */}
        <div class="btn-toolbar navbar-top-links">
          <div class="general-search input-group d-none" id="general-search-section">
            <button class="general-search-btn input-group-text" onclick={handleSearch}>
              <i class="search-icon fa-solid fa-magnifying-glass"></i>
            </button>
            <input
              id="general-search-input"
              type="text"
              class="form-control"
              placeholder="Search ..."
              onkeypress={handleKeyPress}
            />
          </div>
          <div class="dropdown">
            <button
              type="button"
              id="userDropdownMenuButton"
              class="btn dropdown-toggle no-dropdown-arrow"
              data-bs-toggle="dropdown"
              aria-haspopup="true"
              aria-expanded="false">
              <i class="username-icon fas fa-user fa-fw" />
              <span id="username" class="d-none d-md-inline display-username">
                {getCurrentUserName()}
              </span>
              <span class="small d-none d-md-inline display-auth-provider" />
            </button>
            <div class="dropdown-menu dropdown-menu-end" aria-labelledby="userDropdownMenuButton">
              <h6 class="dropdown-header display-username d-md-none">{getCurrentUserName()}</h6>
              <a id="userDropdownItemChangePassword" class="dropdown-item d-none" href="#changepassword">
                <i class="fas fa-pencil-alt" /> Change your Portal Password
              </a>
              <a
                id="userDropdownItemTempPrivilegeEscalation"
                class="dropdown-item d-none"
                href="#"
                onclick={showTempPrivilegeEscalationRequestModal}>
                <i class="fas fa-pencil-alt" /> Temporary Privilege Escalation
              </a>
              <a id="userDropdownItemManageAPIKeys" class="dropdown-item" href={linkWithoutSearch('#manageapikeys')}>
                <i class="fas fa-cog" /> Manage your Portal API Keys
              </a>
              <a
                id="userDropdownItemManageAPIKeys"
                class="dropdown-item"
                href={linkWithoutSearch('#managem2mportalapiaccess')}>
                <i class="fas fa-cog" /> Manage M2M Client Portal API Access
              </a>
              <a
                id="userDropdownItemLogOut"
                role="button"
                class="dropdown-item"
                href="#"
                onclick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  logoutFromIdp();
                }}>
                <i class="fas fa-power-off" /> Logout
              </a>
            </div>
          </div>

          <button
            type="button"
            class="btn navbar-toggler d-md-none"
            style="color: lightgrey"
            data-bs-toggle="collapse"
            data-bs-target="#sidebar">
            <i class="fas fa-bars" />
          </button>
        </div>
      </nav>

      <div id="page-wrapper">
        {/* Sidebar */}
        <Sidebar />

        {/* Page Content */}
        <div id="page-content-wrapper">
          <div id="banners-container" />

          <div class="container-fluid">
            <div id="page-header-row" class="row">
              <div class="col-lg-12">
                <h1 class="page-header" id="mainTitle" />
              </div>
            </div>

            <div id="mainPage">
              {/* Loading indicator — viewport-centered so it's not offset by the hidden sidebar */}
              <div class="loading-portal-overlay">
                <div class="text-center">
                  <div class="spinner-border text-primary mb-3" style="width: 3rem; height: 3rem;" role="status">
                    <span class="visually-hidden">Loading...</span>
                  </div>
                  <div style="font-size: 1.5rem; font-weight: 500; color: #333;">Loading portal...</div>
                  <div class="mt-2" style="font-size: 1.1rem; color: #555;">
                    Fetching your permissions and accounts — this may take a few seconds
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* GAIA Chat Widget — floating assistant available on all pages */}
      <GaiaChatWidget />

      {/* Toasts */}
      <div class="toast-container position-absolute portal" id="toastPlacement" />

      {/* Footer */}
      <nav class="navbar navbar-inverse fixed-bottom" style="justify-content: flex-start;">
        <div
          class="btn-group dropup version-footer-group"
          data-tooltip="Click to view changelogs of current and past versions">
          <a class="navbar-brand" href="#releasenotes" data-bs-toggle="modal">
            <span id="portal-version" />
            <small>
              &nbsp;(
              <span id="version-bottom" />)
            </small>
          </a>
          <button
            type="button"
            class="btn btn-secondary dropdown-toggle dropdown-toggle-split"
            data-bs-toggle="dropdown"
            aria-expanded="false">
            <span class="visually-hidden">Toggle Dropdown</span>
          </button>
          <ul class="dropdown-menu" id="ms-releasenotes-container"></ul>
        </div>
      </nav>

      {/* Release Notes */}
      <div class="container" id="changelog" />
      <div id="ms-changelog-container"></div>

      <div class="loading-modal">
        <div class="loading-modal-container">
          <div class="spinner-border" style="width:4rem;height:4rem;" role="status" />
        </div>
      </div>

      {/* Modal Popup */}
      <div id="sharedModal" class="modal fade" role="dialog" tabindex="-1" aria-hidden="true">
        <div id="sharedModalDialog" class="modal-dialog modal-dialog-centered modal-dialog-scrollable">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title" id="sharedModalTitle">
                Modal title
              </h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close" />
            </div>

            <div class="modal-body" id="sharedModalContent" />

            <div class="modal-footer" id="sharedModalFooter">
              <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Temporary Role Modal Popup */}
      <div id="temporaryRoleModal" class="modal fade" role="dialog" tabindex="-1" aria-hidden="true">
        <div id="temporaryRoleModalDialog" class="modal-dialog modal-dialog-centered modal-dialog-scrollable">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title" id="temporaryRoleModalTitle">
                <strong>Temporary Account Role Request</strong>
              </h5>
              <button type="button" class="btn-close" id="close-modal-button" aria-label="Close" />
            </div>

            <div class="modal-body" id="temporaryRoleModalContent" />

            <div class="modal-footer" id="temporaryRoleModalFooter">
              <button type="button" class="btn btn-outline-secondary" id="save-modal-button">
                Close and save
              </button>
              <button type="button" class="btn btn-success" id="copy-modal-button">
                Copy link to clipboard
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Distribution List Wizard Modal */}
      <div
        id="dlWizard"
        className="modal fade"
        data-bs-backdrop="static"
        data-bs-keyboard="false"
        tabIndex="-1"
        aria-labelledby="dlWizardTitle"
        aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="dlWizardTitle" />
              <button type="button" className="btn-close close-wizard" data-bs-dismiss="modal" aria-label="Close" />
            </div>
            <div className="modal-body" id="dlWizardBody" />
            <div className="modal-footer" id="dlWizardFooter">
              <button type="button" className="btn btn-outline-secondary close-wizard" data-bs-dismiss="modal">
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
